import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { HttpParams } from '@capacitor/core';
import { Directory } from '@capacitor/filesystem';
import { DataService } from './data.service';
import { EMPTY, Observable, Subject, catchError, concatMap, defer, filter, firstValueFrom, map, merge, mergeMap, of, take, tap } from 'rxjs';
import { ApiReturn_Levage, App_FieldListenerEntry, App_FieldListenerName, App_FormCreatorJson, App_formDoc, App_FormSketchField, App_FormSketchFormDynamique, App_FormStep, App_Levage, App_Levage_Updateable, App_LevageSend, App_TrueField } from 'src/app/utils/interfaces';
import { HttpService } from 'src/app/services/app/http.service';
import { EmbedContextService } from './embed-context.service';
import { SiteService } from './site.service';
import { Enum_LevageStatus } from 'src/app/utils/enums';
import { buildDisableNewConstraintResults, evaluateInitialvalue, evaluateInitialValueDynamicForm, instanceOfFieldListe, instanceOfTrueField, isValidDate, readonlyRuleApplies } from 'src/app/utils/utils';
import { Type_App_AsyncValidator_CheckAutorisationsHabilitations, Type_App_Validator, Type_App_Validator_ValueDifferentFrom, Type_App_Validator_ValueNotEqualTo, Type_Field, Type_NoField } from 'src/app/utils/types';
import { FileService } from 'src/app/services/app/file.service';
import { Md5 } from 'md5-typescript';

@Injectable({
  providedIn: 'root'
})
export class SafeLiftService {

  private sendPendingInProgress: Promise<ApiReturn_Levage[]> | null = null;

  constructor(
    private dataService: DataService,
    private embedContextService: EmbedContextService,
    private fileService: FileService,
    private formBuilder: FormBuilder,
    private httpService: HttpService,
    private siteService: SiteService,
  ) { }

  /** Erreurs du contrôle alignées sur des validateurs ayant triggerAlert (points bloquants). */
  private controlHasTriggerAlertError(fieldMeta: Type_Field, ctrl: AbstractControl): boolean {
    if (!fieldMeta.checkValidity) {
      return false;
    }
    const validators = fieldMeta.checkValidity.validators || [];
    const asyncValidators = fieldMeta.checkValidity.asyncValidators || [];
    const allValidators = [...validators, ...asyncValidators];
    const validatorsWithTriggerAlert = allValidators.filter(validator => validator.triggerAlert);
    if (validatorsWithTriggerAlert.length === 0) {
      return false;
    }
    const errors = ctrl.errors;
    if (errors === null) {
      return false;
    }
    return !!(
      validatorsWithTriggerAlert.find(validator => errors[validator.type]) ||
      (errors['err'] !== undefined &&
        errors['err'][`type`] !== undefined &&
        validatorsWithTriggerAlert.find(validator => errors['err'][`type`] === validator.type))
    );
  }

  /** drawing / image / markers du croquis : points bloquants triggerAlert sur sous-contrôles. */
  private sketchRootHasNestedTriggerAlertErrors(sketchField: App_FormSketchField, fieldEntity: FormGroup): boolean {
    const parts: Type_Field[] = [];
    const f = sketchField.fields;
    if (f.image && typeof f.image !== 'string' && f.image.name) {
      parts.push(f.image as Type_Field);
    }
    if (f.drawing && typeof f.drawing !== 'string' && f.drawing.name) {
      parts.push(f.drawing as Type_Field);
    }
    if (f.markers && typeof f.markers !== 'string' && f.markers.name) {
      parts.push(f.markers as Type_Field);
    }
    for (const meta of parts) {
      const ctrl = fieldEntity.controls[meta.name];
      if (ctrl instanceof AbstractControl && ctrl.invalid && this.controlHasTriggerAlertError(meta, ctrl)) {
        return true;
      }
    }
    return false;
  }

  interrogateForm(
    display: App_FormCreatorJson,
    forms: FormGroup,
    stepFormGroup?: FormGroup,
    options?: { skipDirtyCheck?: boolean; etatId?: number },
  ): {
    blocName: string;
    fieldName: string;
    fieldFormControl: AbstractControl<any, any>;
    field: App_TrueField | App_FormSketchField;
  }[] {
    const skipDirtyCheck = options?.skipDirtyCheck === true;
    const steps = display.steps;
    const stepsToInterrogate: App_FormStep[] = stepFormGroup
      ? (() => {
          for (const step of steps) {
            if (forms.controls[step.name] === stepFormGroup) {
              return [step];
            }
          }
          return steps;
        })()
      : steps;
    const pointsCritiquesNc: { blocName: string; fieldName: string; fieldFormControl: AbstractControl<any, any>; field: App_TrueField | App_FormSketchField }[] = [];
    stepsToInterrogate.forEach(step => {
      const stepGroup = forms.controls[step.name];
      if (stepGroup instanceof FormGroup) {
        step.blocs.forEach(bloc => {
          const blocGroup = stepGroup.controls[bloc.name];
          if (blocGroup instanceof FormGroup) {
            bloc.fields.forEach(field => {
              if (field.type === 'field' || field.type === 'custom-field') {
                const fieldEntity = blocGroup.controls[field.name];
                if (fieldEntity instanceof AbstractControl && fieldEntity.invalid) {
                  const isTrueField = field.type === 'field';
                  if (isTrueField) {
                    const trueField = field as App_TrueField;
                    const isHidden = this.isFieldHidden(trueField, forms, display);
                    if (isHidden) {
                      return;
                    }
                    if (field.disable?.hideIfDisabled === true && fieldEntity.disabled) {
                      return;
                    }
                  } else if (field.type === 'custom-field' && field.subType === 'sketch') {
                    if (this.isFieldHidden(field as App_FormSketchField, forms, display)) {
                      return;
                    } 
                  }
                  // readonly 1 + déjà visible (non sorti par isFieldHidden) : remonter l’erreur NOGO comme un champ normal.
                  if (field.type === 'field') {
                    const ro = Number((field as App_TrueField & { readonly?: 0 | 1 | 2 | 3 }).readonly ?? 0);
                    if (readonlyRuleApplies(ro, options?.etatId) && ro !== 1) {
                      return;
                    }
                  } else if (field.type === 'custom-field' && field.subType === 'sketch') {
                    const ro = Number((field as App_FormSketchField).readonly ?? 0);
                    if (readonlyRuleApplies(ro, options?.etatId) && ro !== 1) {
                      return;
                    }
                  }
                  const shouldInspectErrors = skipDirtyCheck || fieldEntity.dirty;
                  if (!shouldInspectErrors) {
                    return;
                  }
                  if (field.type === 'field') {
                    if (field.checkValidity) {
                      const validators = field.checkValidity.validators || [];
                      const asyncValidators = field.checkValidity.asyncValidators || [];
                      const allValidators = [...validators, ...asyncValidators];
                      const validatorsWithTriggerAlert = allValidators.filter(validator => validator.triggerAlert);
                      if (validatorsWithTriggerAlert.length > 0) {
                        if (
                          field.subType === 'checkbox' ||
                          field.subType === 'drawing' ||
                          field.subType === 'input-number' ||
                          field.subType === 'input-text' ||
                          field.subType === 'liste' ||
                          field.subType === 'radio' ||
                          field.subType === 'textarea'
                        ) {
                          const errors = fieldEntity.errors;
                          if (
                            errors !== null &&
                            (validatorsWithTriggerAlert.find(validator => errors[validator.type]) ||
                              (errors['err'] !== undefined &&
                                errors['err'][`type`] !== undefined &&
                                validatorsWithTriggerAlert.find(validator => errors['err'][`type`] === validator.type)))
                          ) {
                            pointsCritiquesNc.push({
                              blocName: bloc.name,
                              fieldName: field.name,
                              fieldFormControl: fieldEntity,
                              field: field,
                            });
                          }
                        } else if (field.subType === 'case_dyn') {
                          if (fieldEntity instanceof FormArray) {
                            const casesControls = fieldEntity.controls;
                            if (
                              casesControls.some(caseControl => {
                                if (!caseControl.invalid) {
                                  return false;
                                }
                                const errors = caseControl.errors;
                                return (
                                  errors !== null &&
                                  (validatorsWithTriggerAlert.find(validator => errors[validator.type]) ||
                                    (errors['err'] !== undefined &&
                                      errors['err'][`type`] !== undefined &&
                                      validatorsWithTriggerAlert.find(validator => errors['err'][`type`] === validator.type)))
                                );
                              })
                            ) {
                              pointsCritiquesNc.push({
                                blocName: bloc.name,
                                fieldName: field.name,
                                fieldFormControl: fieldEntity,
                                field: field,
                              });
                            }
                          }
                        }
                      }
                    }
                  } else if (field.type === 'custom-field') {
                    if (field.subType === 'sketch' && fieldEntity instanceof FormGroup) {
                      const sketchField = field as App_FormSketchField;
                      let fromPrototype = false;
                      const prototypeOnAddMarker = field.fields.prototypeOnAddMarker;
                      if (prototypeOnAddMarker) {
                        const prototypesFormArr = fieldEntity.controls[prototypeOnAddMarker.name];
                        if (prototypesFormArr instanceof FormArray && prototypesFormArr.invalid) {
                          const prot = prototypeOnAddMarker.champs;
                          const filteredGroupsChamps = prot.filter(f => {
                            if (f.type !== 'field') {
                              return false;
                            }
                            const acheckValidity = f.checkValidity;
                            if (!acheckValidity) {
                              return false;
                            }
                            const avalidators = acheckValidity.validators || [];
                            const aasyncValidators = acheckValidity.asyncValidators || [];
                            const aallValidators = [...avalidators, ...aasyncValidators];
                            return aallValidators.find(validator => validator.triggerAlert) !== undefined;
                          }) as Type_Field[];
                          if (filteredGroupsChamps.length > 0) {
                            const groupes = prototypesFormArr.controls;
                            fromPrototype = groupes.some(groupe => {
                              if (!(groupe instanceof FormGroup)) {
                                return false;
                              }
                              for (const ch of filteredGroupsChamps) {
                                const fld = groupe.controls[ch.name];
                                if (fld instanceof AbstractControl) {
                                  const acheckValidity = ch.checkValidity;
                                  const avalidators = acheckValidity?.validators || [];
                                  const aasyncValidators = acheckValidity?.asyncValidators || [];
                                  const aallValidators = [...avalidators, ...aasyncValidators];
                                  const avalidatorsWithTriggerAlert = aallValidators.filter(validator => validator.triggerAlert);
                                  const errors = fld.errors;
                                  if (
                                    errors !== null &&
                                    (avalidatorsWithTriggerAlert.find(validator => errors[validator.type]) ||
                                      (errors['err'] !== undefined &&
                                        errors['err'][`type`] !== undefined &&
                                        avalidatorsWithTriggerAlert.find(validator => errors['err'][`type`] === validator.type)))
                                  ) {
                                    return true;
                                  }
                                }
                              }
                              return false;
                            });
                          }
                        }
                      }
                      const fromNestedRoots = this.sketchRootHasNestedTriggerAlertErrors(sketchField, fieldEntity);
                      if (fromPrototype || fromNestedRoots) {
                        pointsCritiquesNc.push({
                          blocName: bloc.name,
                          fieldName: field.name,
                          fieldFormControl: fieldEntity,
                          field: field,
                        });
                      }
                    }
                  }
                }
              }
            });
          }
        });
      }
    });
    return pointsCritiquesNc;
  }

  /**
   * Évalue de manière synchrone si un champ devrait être caché en fonction de ses conditions disable_new
   * Retourne true si le champ est caché, false s'il est visible
   */
  isFieldHidden(field: App_TrueField | App_FormSketchField, forms: FormGroup, display?: App_FormCreatorJson): boolean {
    const disableNew = (field as any).disable_new;
    
    // Si pas de disable_new, le champ est visible (donc pas caché)
    if (!disableNew) {
      return false;
    }
    
    // Si hideIfDisabled est false, le champ est toujours visible
    if (disableNew.hideIfDisabled !== true) {
      return false;
    }
    
    // Évaluer l'opération pour déterminer la visibilité
    try {
      const operation = disableNew.operation;
      const constraints = disableNew.constraints;
      
      if (!operation || !Array.isArray(constraints) || constraints.length === 0) {
        return false; // Par défaut visible si configuration invalide
      }

      const arrayOfTransformedValues = buildDisableNewConstraintResults(
        operation,
        constraints,
        (champ: string) => {
          const sourceField = this.findFieldInFormEntity(forms, champ);
          return sourceField instanceof AbstractControl ? sourceField : undefined;
        },
        (champ: string) => (display ? this.findFieldinFormParams(display, champ) : undefined),
      );
      
      // Évaluer l'opération finale
      const operationReplaced = operation.replace(/\$[\w]+/g, (str: string) => {
        const found = arrayOfTransformedValues.find(v => v?.id === str);
        if (found) {
          return found.value.toString();
        }
        return 'false';
      });
      
      const evaluated = eval(operationReplaced);
      // Si l'évaluation est true, le champ est VISIBLE (donc pas caché)
      // Si l'évaluation est false, le champ est CACHÉ
      return evaluated !== true;
    } catch (error) {
      return false; // Par défaut visible en cas d'erreur
    }
  }

  fetchAndStoreFormulairesDocuments(formulaires: App_FormCreatorJson[]) {
    return new Promise((resolve, reject) => {
      const docs = this.getFormulairesDocuments(formulaires)

      let index = 0;
      const simultaneous = 5;
      const fiveFirst = docs.slice(0, simultaneous);
      const subsequents = docs.slice(simultaneous);
      const results: { doc: App_formDoc, success: boolean }[] = [];
      const emitter$: Subject<number> = new Subject();

      const checkfilePresenceFetchIfNecessary$ = (doc: App_formDoc) => defer(() => this.fileService.readFile(`documents/${Md5.init(doc.web)}`, Directory.Data)).pipe(
        map(_ => ({ doc, success: true })),
        catchError(err => this.fetchDocument(doc).pipe(
          map(_ => ({ doc, success: true })),
          catchError(err => of({ doc, success: false }))
        ))
      )

      const request$ = (elem: App_formDoc) => checkfilePresenceFetchIfNecessary$(elem).pipe(
        tap(_ => results.push(_)),
        map(_ => elem),
        tap(_ => {
          emitter$.next(index++);
        })
      );

      merge(
        ...fiveFirst.map(e => request$(e)),
        emitter$.pipe(
          filter(i => i <= subsequents.length),
          mergeMap(i => {
            const elem = subsequents[i];
            if (elem) {
              return request$(elem)
            } else {
              return EMPTY;
            }
          })
        )
      ).pipe(
        take(docs.length),
      ).subscribe({
        complete: () => resolve(results)
      })
    });
  }

  fetchAndStoreLevagesDocuments(levages: App_Levage_Updateable[]) {
    return new Promise((resolve, reject) => {
      const docs = this.getLevagesDocuments(levages)
      let index = 0;
      const simultaneous = 5;
      const fiveFirst = docs.slice(0, simultaneous);
      const subsequents = docs.slice(simultaneous);
      const results: { doc: App_formDoc, success: boolean }[] = [];
      const emitter$: Subject<number> = new Subject();

      const checkfilePresenceFetchIfNecessary$ = (doc: App_formDoc) => defer(() => this.fileService.readFile(`documents/${Md5.init(doc.web)}`, Directory.Data)).pipe(
        map(_ => ({ doc, success: true })),
        catchError(err => this.fetchDocument(doc).pipe(
          map(_ => ({ doc, success: true })),
          catchError(err => of({ doc, success: false }))
        ))
      )

      const request$ = (elem: App_formDoc) => checkfilePresenceFetchIfNecessary$(elem).pipe(
        tap(_ => results.push(_)),
        map(_ => elem),
        tap(_ => {
          emitter$.next(index++);
        })
      );

      merge(
        ...fiveFirst.map(e => request$(e)),
        emitter$.pipe(
          filter(i => i <= subsequents.length),
          mergeMap(i => {
            const elem = subsequents[i];
            if (elem) {
              return request$(elem)
            } else {
              return EMPTY;
            }
          })
        )
      ).pipe(
        take(docs.length),
      ).subscribe({
        complete: () => resolve(results)
      })
    });
  }

  fetchDocument(doc: App_formDoc) {
    const nameAsMd5 = `documents/${Md5.init(doc.web)}`;
    return this.httpService.downloadDocument(doc.web).pipe(
      concatMap(base64 => defer(() => this.fileService.saveFile(base64, nameAsMd5, Directory.Data)))
    )
  }

  fetchLevageListe(token: string, siteId: number): Observable<ApiReturn_Levage[]> {
    const params: HttpParams = { site_id: `${siteId}` };
    const _header: [string, string][] = [['Authorization', `Bearer ${token}`]];
    return this.httpService.apiGet<ApiReturn_Levage[]>({ uriTail: `/levage/list` }, _header, params);
  }

  fetchLevageToValidateListe(token: string, siteId: number): Observable<ApiReturn_Levage[]> {
    const params: HttpParams = { site_id: `${siteId}` };
    const _header: [string, string][] = [['Authorization', `Bearer ${token}`]];
    return this.httpService.apiGet<ApiReturn_Levage[]>({ uriTail: `/levage/list-a-valider` }, _header, params);
  }

  fetchFormulaireListe(token: string, siteId: number): Observable<App_FormCreatorJson[]> {
    const params: HttpParams = { site_id: `${siteId}` };
    const _header: [string, string][] = [['Authorization', `Bearer ${token}`]];
    console.log('[DEBUG] fetchFormulaireListe called with params:', params); // 👈 LOG ICI
    return this.httpService.apiGet<App_FormCreatorJson[]>({ uriTail: `/formulaire/list` }, _header, params);
  }

  getAllLevages(): Observable<App_Levage_Updateable[]> {
    return this.embedContextService.getEmbedContext().pipe(
      concatMap(embedContext => embedContext
        ? this.getAllLevagesFromApi()
        : this.getAllLevagesFromStorage()
      )
    );
  }

  getAllLevagesFromApi(): Observable<App_Levage_Updateable[]> {
    return defer(() => this.httpService.dataGetUserToken()).pipe(
      concatMap(token => this.siteService.getSiteId().pipe(
        concatMap(siteId => siteId
          ? this.fetchLevageListe(token, siteId)
          : of([])
        )
      )),
      map(levages => levages.map(levage => ({
        app_update: {
          toUpdate: false
        },
        data: levage
      })))
    );
  }

  getFormulairesDocuments(formulaires: App_FormCreatorJson[]) {
    return formulaires.reduce<App_formDoc[]>((docs, form) => {
      return form.documents ?
      [
        ...docs,
        ...form.documents
      ]
      : docs
    }, []);
  }

  getLevagesDocuments(levages: App_Levage_Updateable[]) {
    return levages.reduce<App_formDoc[]>((docs, levage) => {
      const levageDocumentsElaboration = levage?.data?.documents_elaboration;
      return Array.isArray(levageDocumentsElaboration)
      ? [
        ...docs,
        ...levageDocumentsElaboration
      ]
      : docs
    }, [])
  }

  getLevagesToValidateListeFromApi() {
    return defer(() => this.httpService.dataGetUserToken()).pipe(
      concatMap(token => this.siteService.getSiteId().pipe(
        concatMap(siteId => siteId
          ? this.fetchLevageToValidateListe(token, siteId)
          : of([])
        )
      ))
    )
  }

  getAllLevagesFromStorage(): Observable<App_Levage_Updateable[]> {
    return this.dataService.levageListe.get().pipe(map(levageListe => levageListe || []));
  }

  getLevage(id: number | string): Observable<App_Levage_Updateable | undefined> {
    return this.embedContextService.getEmbedContext().pipe(
      concatMap(embedContext => embedContext
        ? this.getLevageFromApi(id)
        : this.getLevageFromStorage(id)
      )
    );
  }

  getLevageFromApi(id: number | string): Observable<App_Levage_Updateable> {
    return defer(() => this.httpService.dataGetUserToken()).pipe(
      concatMap(token => {
        const _header: [string, string][] = [['Authorization', `Bearer ${token}`]];
        const uriTail = `/levage/get`;
        const credentials = { id };
        return this.httpService.apiPost<{ id: string | number }, ApiReturn_Levage>({ uriTail }, credentials, _header).pipe(
          tap((returnedLevage) => {
            console.log('[DEBUG] levage API response:', returnedLevage); // 👈 LOG ICI
          }),
          map(returnedLevage => ({
            app_update: {
              toUpdate: false
            },
            data: returnedLevage
          }))
        )
      })
    )
  }

  getLevageFromStorage(id: number | string): Observable<App_Levage_Updateable | undefined> {
    return this.getAllLevagesFromStorage().pipe(map(levageListe => levageListe.find(levage => levage.data.id === id)));
  }

  getAllPlansDeLevage(): Observable<App_FormCreatorJson[]> {
    return this.embedContextService.getEmbedContext().pipe(
      concatMap(embedContext => embedContext
        ? this.getAllPlansDeLevageFromApi()
        : this.getAllPlansDeLevageFromStorage()
      )
    );
  }

  getAllPlansDeLevageFromApi(): Observable<App_FormCreatorJson[]> {
    return defer(() => this.httpService.dataGetUserToken()).pipe(
      concatMap(token => this.siteService.getSiteId().pipe(
        concatMap(siteId => siteId
          ? this.fetchFormulaireListe(token, siteId)
          : of([])
        )
      ))
    );
  }

  getAllPlansDeLevageFromStorage(): Observable<App_FormCreatorJson[]> {
    return this.dataService.planLevageListe.get().pipe(map(planLevageListe => planLevageListe || []));
  }

  getPlanDeLevage(id: number, siteIdHint?: number | null): Observable<App_FormCreatorJson | undefined> {
    const getFromApi = () => this.getPlanDeLevageFromApi(id, siteIdHint).pipe(catchError(err => of(undefined)))

    return this.embedContextService.getEmbedContext().pipe(
      concatMap(embedContext => embedContext
        ? getFromApi()
        : this.getPlanDeLevageFromStorage(id).pipe(concatMap(res => res !== undefined
          ? of(res)
          : getFromApi()
        ))
      )
    );
  }

  /**
   * @param siteIdHint Si renseigné (entier > 0), utilisé pour `site_id` à la place du site stocké (ex. levage en cours en embed).
   */
  getPlanDeLevageFromApi(id: number, siteIdHint?: number | null): Observable<App_FormCreatorJson> {
    return defer(() => this.httpService.dataGetUserToken()).pipe(
      concatMap(token => {
        const siteId$ =
          siteIdHint != null && Number.isInteger(siteIdHint) && siteIdHint > 0
            ? of(siteIdHint)
            : this.siteService.getSiteId();
        return siteId$.pipe(
          concatMap(siteId => {
            const _header: [string, string][] = [['Authorization', `Bearer ${token}`]];
            const uriTail = `/formulaire/get`;
            const credentials = siteId != null ? { id, site_id: siteId } : { id };
            return this.httpService.apiPost<{ id: string | number; site_id?: number }, App_FormCreatorJson>({ uriTail }, credentials, _header);
          })
        );
      })
    );
  }

  getPlanDeLevageFromStorage(id: number): Observable<App_FormCreatorJson | undefined> {
    return this.getAllPlansDeLevageFromStorage().pipe(map(planLevageListe => planLevageListe.find(planLevage => planLevage.id === id)));
  }

  sendThenStoreLevage(dataToSend: App_LevageSend, idElementToReplace: number | string | undefined): Promise<App_Levage_Updateable> {
    return new Promise((resolve, reject) => {
      this.sendLevage(dataToSend)
      .then(returnedLevage => this.convertReturnedLevageIntoLevageUpdateable(returnedLevage))
      .then(returnedLevage => {
        this.fetchAndStoreLevagesDocuments([returnedLevage]);
        return this.addOrUpdateLevageInStore(returnedLevage, idElementToReplace)
      })
      .then(_ => resolve(_))
      .catch(_ => {
        // TODO-pf? retour messages d'erreur
        reject(_);
      });
    })
  }

  sendThenRemoveLevage(dataToSend: App_LevageSend): Promise<null> {
    return new Promise((resolve, reject) => {
      this.sendLevage(dataToSend)
      .then(returnedLevage => this.removeLevageInStore(returnedLevage.id).then(_ => resolve(_)))
      .catch(_ => {
        // TODO-pf? retour messages d'erreur
        reject(_);
      });
    })

  }

  sendLevage(dataToSend: App_LevageSend) {
    return this.httpService.dataGetUserToken().then(token => {
      const _header: [string, string][] = [['Authorization', `Bearer ${token}`]];
      const uriTail = `/levage/save`;
      return firstValueFrom(this.httpService.apiPost<App_LevageSend, ApiReturn_Levage>({ uriTail }, dataToSend, _header));
    })
  }

  /**
   * Met à jour le statut d'un levage avec un commentaire optionnel
   * Utilisé pour renvoyer à corriger ou invalider une élaboration
   */
  updateLevageStatus(levageId: number, newStatus: number, comment?: string): Promise<any> {
    return this.httpService.dataGetUserToken().then(token => {
      const _header: [string, string][] = [['Authorization', `Bearer ${token}`]];
      const uriTail = `/levage/update-status`;
      const payload = {
        levage_id: levageId,
        etat_id: newStatus,
        comment: comment || ''
      };
      return firstValueFrom(this.httpService.apiPost<typeof payload, any>({ uriTail }, payload, _header));
    });
  }

  storeLevage(dataToStore: App_LevageSend, form: App_FormCreatorJson | undefined, existingLevage: App_Levage_Updateable | undefined, idElementToReplace: number | string | undefined): Promise<App_Levage_Updateable> {
    return new Promise(async (resolve, reject) => {
      const userInfos = await firstValueFrom(this.dataService.user.get());
      const responsableNom = userInfos?.name || '';
      const editedDatas = dataToStore.datas.reduce<{ name: string; value: any; id?: number; }[]>((datas, toStoreData) => {
        const foundInExistingDatasIndex = datas.findIndex(field => field.name === toStoreData.name);
        if (foundInExistingDatasIndex !== -1) {
          return [
            ...datas.slice(0, foundInExistingDatasIndex),
            {
              ...datas[foundInExistingDatasIndex],
              value: toStoreData.value
            },
            ...datas.slice(foundInExistingDatasIndex + 1)
          ]
        }
        return [...datas, toStoreData];
      }, (existingLevage?.data.datas || []))
      console.log('dataToStore', dataToStore.etat_id);
      console.log('existingLevage', existingLevage?.data.id);

      const pendingData: App_LevageSend = {
        ...dataToStore,
        ...(typeof existingLevage?.data?.id === 'number' ? { levage_id: existingLevage.data.id } : {}),
      };

      const levageToStore: App_Levage_Updateable = existingLevage
      ? {
        app_update: {
          toUpdate: true,
          data: pendingData,
        },
        data: {
          ...existingLevage.data,
          etat: dataToStore.etat_id === Enum_LevageStatus.PREPARATION ? 'Préparation' : Enum_LevageStatus.EN_COURS ? 'En cours' : Enum_LevageStatus.PREPARATION_EN_COURS_DE_VALIDATION ? 'À valider' : '',
          etat_id: dataToStore.etat_id,
          specifique: typeof existingLevage?.data?.specifique === 'boolean'
          ? existingLevage.data.specifique ? 1 : 0
          : typeof existingLevage?.data?.specifique === 'number'
            ? existingLevage.data.specifique
            : 0,
          datas: editedDatas,
          step: dataToStore.step,
          levage_label: dataToStore.levage_label,
          levage_validations: dataToStore.levage_validations || null,
          documents_elaboration: existingLevage.data.documents_elaboration,
          // ...(existingLevage.data.etat_id === Enum_LevageStatus.PREPARATION_EN_COURS_DE_VALIDATION
          // ? {
          //     etat: dataToStore.etat_id === Enum_LevageStatus.PREPARATION ? 'Préparation' : Enum_LevageStatus.EN_COURS ? 'En cours' : Enum_LevageStatus.PREPARATION_EN_COURS_DE_VALIDATION ? 'À valider' : '',
          //     etat_id: dataToStore.etat_id,
          //   }
          // : {}),
        }
      }
      : {
        app_update: {
          toUpdate: true,
          data: pendingData
        },
        data: {
          ...dataToStore,
          specifique: typeof dataToStore?.specifique === 'boolean'
          ? dataToStore.specifique ? 1 : 0
          : typeof dataToStore?.specifique === 'number'
            ? dataToStore.specifique
            : 0,
          id: this.generateFakeId(),
          reference: this.generateFakeReference(),
          numero_ot: null,
          created_at: this.generateFakeCreatedAt(),
          site_label: '',
          site_id: dataToStore.site_id,
          levage_label: dataToStore.levage_label,
          responsable_nom: responsableNom,
          responsable_prenom: '',
          responsable_trigramme: '',
          type_form: form?.label || '',
          form_id: dataToStore.form_id,
          etat: dataToStore.etat_id === Enum_LevageStatus.PREPARATION ? 'Préparation' : Enum_LevageStatus.EN_COURS ? 'En cours' : Enum_LevageStatus.PREPARATION_EN_COURS_DE_VALIDATION ? 'À valider' : '',
          etat_id: dataToStore.etat_id,
          step: dataToStore.step,
          datas: editedDatas,
          levage_validations: dataToStore.levage_validations || null,
          documents_elaboration: [],
          documents: [],
        }
      };
      this.addOrUpdateLevageInStore(levageToStore, idElementToReplace)
      .then(levage => {
        if (form) {
          const entries = this.collectFieldListenerEntriesFromForm(form, levage.data.id);
          if (entries.length) return this.mergeFieldListenersInStorage(entries).then(() => levage);
        }
        return levage;
      })
      .then(levage => resolve(levage))
      .catch(err => reject(err))
    })
  }

  addOrUpdateArrayOfLevagesInStore(levagesToStore: App_Levage_Updateable[]) {
    return firstValueFrom(this.embedContextService.getEmbedContext()).then(embedContext => firstValueFrom(this.getAllLevagesFromStorage())
      .then(levageListe => levageListe || [])
      .then(levageListeFromStorage => {
        const listToStore = levagesToStore.reduce<App_Levage_Updateable[]>((levageListe, levageToStore) => {
          const idElementToReplace = levageToStore.data.id;
          const foundInListeIndex = typeof idElementToReplace === 'number' || typeof idElementToReplace === 'string'
          ? levageListe.findIndex(levage => levage.data.id === idElementToReplace)
          : -1
          const deleteLevage = !embedContext && (levageToStore.data.etat_id === Enum_LevageStatus.TERMINE || levageToStore.data.etat_id === Enum_LevageStatus.TERMINE_NON_REALISE)

          const listToStore: App_Levage_Updateable[] = deleteLevage
          ? foundInListeIndex !== -1
            ? [
              ...levageListe.slice(0, foundInListeIndex),
              ...levageListe.slice(foundInListeIndex + 1)
            ]
            : [
              ...levageListe
            ]
          : foundInListeIndex !== -1
            ? [
              ...levageListe.slice(0, foundInListeIndex),
              levageToStore,
              ...levageListe.slice(foundInListeIndex + 1)
            ]
            : [
              ...levageListe,
              levageToStore
            ];
          return listToStore;
        }, levageListeFromStorage);
        return this.dataService.levageListe.set(listToStore);
      })
    )
  }

  addOrUpdateLevageInStore(levageToStore: App_Levage_Updateable, idElementToReplace: number | string | undefined): Promise<App_Levage_Updateable> {
    return firstValueFrom(this.embedContextService.getEmbedContext()).then(embedContext => firstValueFrom(this.getAllLevagesFromStorage())
      .then(levageListe => levageListe || [])
      .then(levageListe => {
        const foundInListeIndex = typeof idElementToReplace === 'number' || typeof idElementToReplace === 'string'
        ? levageListe.findIndex(levage => levage.data.id === idElementToReplace)
        : -1
        //const deleteLevage = !embedContext && (levageToStore.data.etat_id === Enum_LevageStatus.TERMINE || levageToStore.data.etat_id === Enum_LevageStatus.TERMINE_NON_REALISE)
        const deleteLevage = false;

        const listToStore: App_Levage_Updateable[] = deleteLevage
        ? foundInListeIndex !== -1
          ? [
            ...levageListe.slice(0, foundInListeIndex),
            ...levageListe.slice(foundInListeIndex + 1)
          ]
          : [
            ...levageListe
          ]
        : foundInListeIndex !== -1
          ? [
            ...levageListe.slice(0, foundInListeIndex),
            levageToStore,
            ...levageListe.slice(foundInListeIndex + 1)
          ]
          : [
            ...levageListe,
            levageToStore
          ];
        return this.dataService.levageListe.set(listToStore).then(_ => {
          return levageToStore
        });
      })
    )

  }

  /**
   * Collecte les entrées listener (id_levage, code_champ, fonction) pour tous les champs du formulaire qui ont un listener.
   */
  collectFieldListenerEntriesFromForm(form: App_FormCreatorJson, idLevage: number | string): App_FieldListenerEntry[] {
    const entries: App_FieldListenerEntry[] = [];
    if (!form?.steps?.length) return entries;
    for (const step of form.steps) {
      if (!step.blocs?.length) continue;
      for (const bloc of step.blocs) {
        if (!bloc.fields?.length) continue;
        for (const field of bloc.fields) {
          if (field.type === 'label') continue;
          const name = 'name' in field ? field.name : undefined;
          const listener = 'listener' in field ? field.listener : undefined;
          if (typeof name === 'string' && listener != null && typeof listener === 'string') {
            entries.push({ id_levage: idLevage, code_champ: name, fonction: listener as App_FieldListenerEntry['fonction'] });
          }
        }
      }
    }
    return entries;
  }

  /**
   * Fusionne de nouvelles entrées listeners dans le storage avec unicité sur (id_levage, code_champ, fonction).
   * Les données restent en storage (pas de purge ici).
   */
  mergeFieldListenersInStorage(newEntries: App_FieldListenerEntry[]): Promise<App_FieldListenerEntry[]> {
    const sameEntry = (a: App_FieldListenerEntry, b: App_FieldListenerEntry) =>
      a.id_levage === b.id_levage && a.code_champ === b.code_champ && a.fonction === b.fonction;
    return firstValueFrom(this.dataService.fieldListenersListe.get())
      .then(current => {
        const list = current ?? [];
        let merged = [...list];
        for (const e of newEntries) {
          if (!merged.some(x => sameEntry(x, e))) merged.push(e);
        }
        return this.dataService.fieldListenersListe.set(merged);
      });
  }

  /**
   * Récupère les listeners enregistrés pour un levage (optionnellement filtrés par fonction).
   */
  getFieldListenersForLevage(levageId: number | string, fonction?: App_FieldListenerName): Promise<App_FieldListenerEntry[]> {
    return firstValueFrom(this.dataService.fieldListenersListe.get())
      .then(list => (list ?? []).filter(e => e.id_levage === levageId && (fonction == null || e.fonction === fonction)));
  }

  /**
   * Récupère la valeur d'un champ pour un levage (prise en compte des données en attente app_update).
   */
  getFieldValueFromLevage(levage: App_Levage_Updateable, code_champ: string): any {
    const fromUpdate = levage.app_update?.data?.datas?.find((d: { name: string; value: any }) => d.name === code_champ);
    if (fromUpdate !== undefined) return fromUpdate.value;
    const fromData = levage.data?.datas?.find((d: { name: string; value: any }) => d.name === code_champ);
    return fromData?.value;
  }

  /**
   * Vérification avant ouverture d’un levage « élaboration à exécuter » : si un listener choixExecutant
   * a une valeur (personne désignée), seul cet exécutant peut ouvrir le levage (avec mot de passe si ce n’est pas lui).
   * À appeler avant d’ouvrir le levage.
   * @returns { allowed: true } si ouverture autorisée, { allowed: false, designatedUserId } si réservé à un autre utilisateur (demande mot de passe côté page).
   */
  async checkChoixExecutantBeforeOpen(levage: App_Levage_Updateable): Promise<{ allowed: boolean; designatedUserId?: number | null }> {
    const entries = await this.getFieldListenersForLevage(levage.data.id, 'choixExecutant');
    if (!entries.length) return { allowed: true };

    for (const entry of entries) {
      const value = this.getFieldValueFromLevage(levage, entry.code_champ);
      if (value === undefined || value === null || value === '') continue;

      const designatedId = typeof value === 'number' ? value : (typeof value === 'string' && /^\d+$/.test(value) ? parseInt(value, 10) : null);
      if (designatedId === null) continue;

      const currentUser = await firstValueFrom(this.dataService.user.get());
      const currentId = currentUser?.id;
      if (currentId !== undefined && currentId === designatedId) return { allowed: true };

      return { allowed: false, designatedUserId: designatedId };
    }
    return { allowed: true };
  }

  /**
   * Listener « choixExecutant » : récupération de la valeur du champ (utilisé après ouverture pour log / futur usage).
   */
  choixExecutant(params: { levage: App_Levage_Updateable; code_champ: string }): void {
    const value = this.getFieldValueFromLevage(params.levage, params.code_champ);
    console.log('[choixExecutant] levage_id=', params.levage.data.id, 'code_champ=', params.code_champ, 'valeur=', value);
  }

  /**
   * Enregistre en storage les listeners pour tous les levages à partir des formulaires (synchro ou liste).
   */
  registerFieldListenersForLevages(levages: App_Levage_Updateable[], formulaires: App_FormCreatorJson[]): Promise<App_FieldListenerEntry[]> {
    const allEntries: App_FieldListenerEntry[] = [];
    for (const levage of levages) {
      const formId = levage.data.form_id;
      const form = formulaires.find(f => f.id === formId);
      if (form) {
        allEntries.push(...this.collectFieldListenerEntriesFromForm(form, levage.data.id));
      }
    }
    return this.mergeFieldListenersInStorage(allEntries);
  }

  prepareFormulaireListe(formulaireListe: App_FormCreatorJson[]): Promise<App_FormCreatorJson[]> {
    return new Promise((resolve, reject) => {
      resolve(formulaireListe);
    });
  }

  // On a une iste d'entrée issue de l'API
  prepareLevageListe(levageListe: ApiReturn_Levage[]): Promise<App_Levage_Updateable[]> {
    // On récupère le contexte embed ou non
    return firstValueFrom(this.embedContextService.getEmbedContext())
      // On récupère la liste des levages dans le storage
      .then(embedContext => firstValueFrom(this.dataService.levageListe.get())
        .then(levageListeFromStorage => levageListeFromStorage || [])
        .then(levageListeFromStorage => {
          // de la liste des levages de l'API, on ne conserve que ceux dont l'etat_id est PREPARATION ou EN_COURS
          const levageListeFromApiFiltree = levageListe.filter(levage => this.storeOrDropLevage(levage))
          // on convertit la liste filtrée en liste de App_Levage_Updateable
          const levageListeFromApiFiltreeAsLevageUpdateable = levageListeFromApiFiltree.map<App_Levage_Updateable>(levage => ({ app_update: { toUpdate: false }, data: levage }))
          const levageListeToStore = levageListeFromStorage.reduce<App_Levage_Updateable[]>((levageListeToKeep, levage) => {
            // On vérifie si le levage de même id existe dans la liste arrivante
            const foundLevageFromApiIndex = levageListeToKeep.findIndex(levageFromStorage => levage.data.id === levageFromStorage.data.id);
            if (foundLevageFromApiIndex === -1) { // Si dans la liste arrivante, le levage de même id n'existe pas, on ne le retient pas
              return levageListeToKeep;
            } else { // Si dans la liste arrivante, le levage de même id existe ...
              const levageFromApi = levageListeToKeep[foundLevageFromApiIndex];
              
              // Si 'specifique' est manquant dans le levage local (storage), on l'ajoute depuis l'API
              const hasSpecifiqueInStorage = 'specifique' in levage.data;
              const hasSpecifiqueInApi = 'specifique' in levageFromApi.data;

              if (!hasSpecifiqueInStorage && hasSpecifiqueInApi) {
                levage.data = {
                  ...levage.data,
                  specifique: levageFromApi.data.specifique
                };
              }


              if (levageFromApi?.data?.etat_id === levage?.data?.etat_id) { // Si les deux levages ont le même état, on conjecture que le levage n'a pas changé de données entretemps et on conserve le local, au cas où on devrait envoyer des datas
                return [
                  ...levageListeToKeep.slice(0, foundLevageFromApiIndex),
                  levage,
                  ...levageListeToKeep.slice(foundLevageFromApiIndex + 1),
                ]
              } else { // Si les levages ont des états différents, les données arrivées sont considerées comme les bonnes et on ne conserve pas les données locales, on conserve les arrivantes
                return levageListeToKeep;
              }
            }
          }, levageListeFromApiFiltreeAsLevageUpdateable);
          return levageListeToStore;
        })
      );
  }

  shallowCompare(obj1: any, obj2: any): boolean {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    return keys1.every(key => obj1[key] === obj2[key]);
  }
  /**
 * Return true si le levage est un levage à conserver dans le storage
 * teste si son etat_id est PREPARATION ou EN_COURS ou ELABORATION_A_CORRIGER ou ELABORATION_A_EXECUTER
 * @param levage ApiReturn_Levage ou App_Levage
 * @returns boolean
 */
  storeOrDropLevage(levage: ApiReturn_Levage | App_Levage): boolean {
    const toKeepEtatListe: Enum_LevageStatus[] = [
      Enum_LevageStatus.PREPARATION,
      Enum_LevageStatus.EN_COURS,
      Enum_LevageStatus.ELABORATION_A_CORRIGER,
      Enum_LevageStatus.ELABORATION_A_EXECUTER,
      Enum_LevageStatus.PREPARATION_EN_COURS_DE_VALIDATION,
    ];
    const etat_id = levage?.etat_id;
    if (typeof etat_id !== 'number') {
      return false;
    }
    return toKeepEtatListe.includes(etat_id);
  }

  storeFormulaireListe(formulaireListe: App_FormCreatorJson[]): Promise<App_FormCreatorJson[]> {
    return this.dataService.planLevageListe.set(formulaireListe);
  }

  storeLevageListe(levageListeToStore: App_Levage_Updateable[]): Promise<App_Levage_Updateable[]> {
    return this.dataService.levageListe.set(levageListeToStore);
  }

  removeLevageInStore(idElementToRemove: string | number): Promise<null> {
    return firstValueFrom(this.getAllLevagesFromStorage())
    .then(levageListe => levageListe || [])
    .then(levageListe => {
      const foundInListeIndex = typeof idElementToRemove === 'number' || typeof idElementToRemove === 'string'
      ? levageListe.findIndex(levage => levage.data.id === idElementToRemove)
      : -1

      const listToStore = foundInListeIndex !== -1
      ? [
        ...levageListe.slice(0, foundInListeIndex),
        ...levageListe.slice(foundInListeIndex + 1)
      ]
      : [
        ...levageListe
      ];
      return this.dataService.levageListe.set(listToStore).then(_ => {
        return null;
      });
    })

  }

  resetLevageListe() {
    return this.dataService.levageListe.remove();
  }

  resetPlanLevageListe() {
    return this.dataService.planLevageListe.remove();
  }

  generateFakeId() {
    const date = new Date();
    return `NEW_${date.getTime()}`
  }

  generateFakeReference() {
    // const date = new Date();
    // const year = `0000${date.getFullYear()}`.slice(-4)
    // const month = `00${date.getMonth() + 1}`.slice(-2)
    // const day = `00${date.getDate()}`.slice(-2)
    // return `BROUILLON_${day}-${month}-${year}`
    return `non défini`;
  }

  // TODO-pf? attention si plus tard on veut que e soit un paramètre gérable
  generateFakeCreatedAt() {
    const date = new Date();
    const year = `0000${date.getFullYear()}`.slice(-4);
    const month = `00${date.getMonth() + 1}`.slice(-2);
    const day = `00${date.getDate()}`.slice(-2);

    const hours = `00${date.getHours()}`.slice(-2);
    const minutes = `00${date.getMinutes()}`.slice(-2);
    const seconds = `00${date.getSeconds()}`.slice(-2);
    console.log(`${year}-${month}-${day} ${hours}:${minutes}:${seconds}`)

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
  }

  generateForm(field: Type_Field | Type_NoField, dataLevage: App_Levage_Updateable | undefined, pageFormFunc: () => FormGroup | undefined, pageObserveSourcesArr: { formControl: FormControl, validator: Type_App_Validator; }[], context: 'preparation' | 'edition') {
    if (field.subType === 'case_dyn') {
      if (context === 'preparation') {
        return this.generateFormArrayCaseDynPreparation(field, dataLevage)
      } else {
        return this.generateFormArrayCaseDynEdition(field, dataLevage, pageFormFunc, pageObserveSourcesArr)
      }
    } else {
      return this.generateFormControl(field, dataLevage, pageFormFunc, pageObserveSourcesArr)
    }
  }

  generateFormControl(field: Type_Field | Type_NoField, dataLevage: App_Levage_Updateable | undefined, pageFormFunc: () => FormGroup | undefined, pageObserveSourcesArr: { formControl: FormControl, validator: Type_App_Validator; }[]) {
    const initialValue = evaluateInitialvalue(field, dataLevage);
    const formControl = this.formBuilder.control(initialValue);
    if (field.checkValidity !== undefined && Array.isArray(field.checkValidity.validators) && field.checkValidity.validators.length > 0) {
      const validatorsFns = field.checkValidity.validators.reduce<ValidatorFn[]>((validators, validatorEntry) => {
        if (validatorEntry.type === `required`) {
          validators.push(Validators.required);
        } else if (validatorEntry.type === `requiredTrue`) { // TODO-pf? retirer ?
          validators.push(this.requiredTrue())
        } else if (validatorEntry.type === `max` && typeof validatorEntry.maxValue === `number`) {
          validators.push(Validators.max(validatorEntry.maxValue));
        } else if (validatorEntry.type === `min` && typeof validatorEntry.minValue === `number`) {
          validators.push(Validators.min(validatorEntry.minValue));
        } else if (validatorEntry.type === `pattern` && typeof validatorEntry.pattern === `string`) {
          validators.push(Validators.pattern(new RegExp(validatorEntry.pattern)));
        } else if (validatorEntry.type === `value-not-equal-to`) {
          validators.push(this.checkValueIsNotEqualTo(validatorEntry))
        } else if (validatorEntry.type === `value-different-from`) {
          validators.push(this.checkValueIsDifferentFromSources(validatorEntry, pageFormFunc))
          pageObserveSourcesArr.push({ formControl: formControl, validator: validatorEntry })
        }
        return validators;
      }, []);
      formControl.addValidators(validatorsFns);
      formControl.updateValueAndValidity();
    }
    if (field.subType === 'input-marker') {
      const validatorsFns = [];
      const datas = field.datas;
      const limitMarkers = field.limitMarkers;
      if (datas !== undefined && datas !== null) {
        const asEntries = Object.entries(datas);
        if (Array.isArray(asEntries) && asEntries.length > 0) {
          validatorsFns.push(this.checkValueIsAnArrayRespectingMin(asEntries.length));
        }
        if (limitMarkers) {
          validatorsFns.push(this.checkValueIsAnArrayRespectingMax(limitMarkers));
        }
      } else {
        if (limitMarkers) {
          validatorsFns.push(this.checkValueIsAnArrayRespectingMax(limitMarkers));
        }
        validatorsFns.push(this.checkValueIsAnArrayOrNull());
      }
      formControl.addValidators(validatorsFns);
      formControl.updateValueAndValidity();
    }
    if (field.checkValidity !== undefined && Array.isArray(field.checkValidity.asyncValidators) && field.checkValidity.asyncValidators.length > 0) {
      const validatorsFns = field.checkValidity.asyncValidators.reduce<AsyncValidatorFn[]>((validators, validatorEntry) => {
        if (validatorEntry.type === `check-autorisations-habilitations`) {
          if (instanceOfFieldListe(field)) {
            validators.push(this.checkAutorisationHabilitation(field.options, validatorEntry))
          }
        }
        return validators;
      }, []);
      formControl.addAsyncValidators(validatorsFns);
      formControl.updateValueAndValidity();
    }
    return formControl;
  }

  generateFormArrayCaseDynPreparation(field: Type_Field, dataLevage: App_Levage_Updateable | undefined) {
    const formsToAdd = (labelInitialValue: string | null, valueInitialValue: boolean) => this.newFormControlForCaseDynPreparation(labelInitialValue, false);
    return this.generateFormArrayCaseDyn(field, dataLevage, formsToAdd);
  }

  generateFormArrayCaseDynEdition(field: Type_Field, dataLevage: App_Levage_Updateable | undefined, pageFormFunc: () => FormGroup | undefined, pageObserveSourcesArr: { formControl: FormControl, validator: Type_App_Validator; }[]) {
    const formsToAdd = (labelInitialValue: string | null, valueInitialValue: boolean) => this.newFormControlForCaseDynEdition(labelInitialValue, valueInitialValue, field, pageFormFunc, pageObserveSourcesArr);
    return this.generateFormArrayCaseDyn(field, dataLevage, formsToAdd);
  }

  generateFormArrayCaseDyn(field: Type_Field, dataLevage: App_Levage_Updateable | undefined, formsToAdd: (labelInitialValue: string | null, valueInitialValue: boolean) => FormGroup<{
    label: FormControl<string | null>;
    value: FormControl<boolean | null>;
  }>) {
    const initialValue = evaluateInitialvalue(field, dataLevage);
    const formArray = this.formBuilder.array<FormGroup<{ label: FormControl<string | null>; value: FormControl<boolean | null>; }>>([]);
    try {
      const parsedInitialValue = JSON.parse(initialValue)
      if (Array.isArray(parsedInitialValue)) {
        for (const element of parsedInitialValue) {
          const formToAdd = formsToAdd(element.label, element.value);
          formArray.push(formToAdd);
        }
      } else {
        if (Array.isArray(initialValue)) {
          for (const element of initialValue) {
            const formToAdd = formsToAdd(element.label, element.value);
            formArray.push(formToAdd);
          }
        }
      }
    } catch (error) {
      return formArray
    }
    return formArray
  }

  newFormControlForCaseDynPreparation(labelInitialValue: string | null, valueInitialValue: boolean) {
    const label: FormControl<string | null> = this.formBuilder.control(labelInitialValue);
    const value: FormControl<boolean | null> = this.formBuilder.control(valueInitialValue);
    const formToAdd: FormGroup<{ label: FormControl<string | null>; value: FormControl<boolean | null> }> = this.formBuilder.group({
      label,
      value
    });
    return formToAdd
  }

  newFormControlForCaseDynEdition(labelInitialValue: string | null, valueInitialValue: boolean, field: Type_Field, pageFormFunc: () => FormGroup | undefined, pageObserveSourcesArr: { formControl: FormControl, validator: Type_App_Validator; }[]) {
    const label: FormControl<string | null> = this.formBuilder.control(labelInitialValue);
    const value: FormControl<boolean | null> = this.formBuilder.control(valueInitialValue);

    if (field.checkValidity !== undefined && Array.isArray(field.checkValidity.validators) && field.checkValidity.validators.length > 0) {
      const validatorsFns = field.checkValidity.validators.reduce<ValidatorFn[]>((validators, validatorEntry) => {
        if (validatorEntry.type === `required`) {
          validators.push(Validators.required);
        } else if (validatorEntry.type === `requiredTrue`) { // TODO-pf? retirer ?
          validators.push(this.requiredTrue())
        } else if (validatorEntry.type === `max` && typeof validatorEntry.maxValue === `number`) {
          validators.push(Validators.max(validatorEntry.maxValue));
        } else if (validatorEntry.type === `pattern` && typeof validatorEntry.pattern === `string`) {
          validators.push(Validators.pattern(new RegExp(validatorEntry.pattern)));
        } else if (validatorEntry.type === `value-not-equal-to`) {
          validators.push(this.checkValueIsNotEqualTo(validatorEntry))
        } else if (validatorEntry.type === `value-different-from`) {
          validators.push(this.checkValueIsDifferentFromSources(validatorEntry, pageFormFunc))
          pageObserveSourcesArr.push({ formControl: value, validator: validatorEntry })
        }
        return validators;
      }, []);
      value.addValidators(validatorsFns);
      value.updateValueAndValidity();
    }
    if (field.checkValidity !== undefined && Array.isArray(field.checkValidity.asyncValidators) && field.checkValidity.asyncValidators.length > 0) {
      const validatorsFns = field.checkValidity.asyncValidators.reduce<AsyncValidatorFn[]>((validators, validatorEntry) => {
        if (validatorEntry.type === `check-autorisations-habilitations`) {
          if (instanceOfFieldListe(field)) {
            validators.push(this.checkAutorisationHabilitation(field.options, validatorEntry))
          }
        }
        return validators;
      }, []);
      value.addAsyncValidators(validatorsFns);
      value.updateValueAndValidity();
    }

    const formToAdd: FormGroup<{ label: FormControl<string | null>; value: FormControl<boolean | null> }> = this.formBuilder.group({
      label,
      value
    });
    return formToAdd
  }

  findFieldInFormEntity(form: FormGroup | FormArray | undefined, fieldName: string): AbstractControl | undefined {
    if (form instanceof FormGroup || form instanceof FormArray) {
      for (const subFormEntity of Object.entries(form.controls)) {
        if (subFormEntity[0] === fieldName) {
          return subFormEntity[1];
        } else {
          if (subFormEntity[1] instanceof FormGroup || subFormEntity[1] instanceof FormArray) {
            const foundField = this.findFieldInFormEntity(subFormEntity[1], fieldName);
            if (foundField) {
              return foundField;
            }
          }
        }
      }
    }
    return undefined;
  }

  findFieldinFormParams(formCreatorJson: App_FormCreatorJson, fieldName: string) {
    if (Array.isArray(formCreatorJson.steps)) {
      for (const step of formCreatorJson.steps) {
        if (Array.isArray(step.blocs)) {
          for (const bloc of step.blocs) {
            if (Array.isArray(bloc.fields)) {
              for (const field of bloc.fields) {
                if (field.name === fieldName) {
                  return field;
                }
              }
            }
          }
        }
      }
    }
    return undefined;
  }

  sendPendingLevages(token: string): Promise<ApiReturn_Levage[]> {
    if (this.sendPendingInProgress) {
      return this.sendPendingInProgress;
    }
    this.sendPendingInProgress = this.doSendPendingLevages(token).finally(() => {
      this.sendPendingInProgress = null;
    });
    return this.sendPendingInProgress;
  }

  private async doSendPendingLevages(token: string): Promise<ApiReturn_Levage[]> {
    await this.reconcilePendingLevages(token);

    const levageListe = await firstValueFrom(this.getAllLevagesFromStorage());
    const levagesToSend = (levageListe || []).filter(
      levage => levage.app_update.toUpdate && levage.app_update.data !== undefined
    );

    const results: ApiReturn_Levage[] = [];
    for (const pending of levagesToSend) {
      const localId = pending.data.id;
      await this.setLevagePendingState(localId, false);
      try {
        const payload = this.buildLevageSendPayload(pending);
        const returned = await this.sendLevage(payload);
        const updateable = this.convertReturnedLevageIntoLevageUpdateable(returned);
        await this.addOrUpdateLevageInStore(updateable, localId);
        await this.reassignFieldListenersAfterIdReplace(localId, returned.id);
        results.push(returned);
      } catch (err) {
        console.error('[sendPendingLevages] échec levage', localId, err);
        await this.setLevagePendingState(localId, true);
      }
    }
    return results;
  }

  reconcilePendingLevages(token: string): Promise<void> {
    return firstValueFrom(this.siteService.getSiteId()).then(siteId => {
      if (!siteId) {
        return firstValueFrom(this.getAllLevagesFromStorage()).then(localList =>
          this.dataService.levageListe.set(this.reconcileLevageListe(localList || [], [])).then(() => undefined)
        );
      }
      return Promise.all([
        firstValueFrom(this.fetchLevageListe(token, siteId).pipe(catchError(() => of([] as ApiReturn_Levage[])))),
        firstValueFrom(this.fetchLevageToValidateListe(token, siteId).pipe(catchError(() => of([] as ApiReturn_Levage[])))),
        firstValueFrom(this.getAllLevagesFromStorage()),
      ]).then(([levages, toValidate, localList]) => {
        const apiList = [...levages, ...toValidate];
        const reconciled = this.reconcileLevageListe(localList || [], apiList);
        return this.dataService.levageListe.set(reconciled).then(() => undefined);
      });
    });
  }

  private reconcileLevageListe(
    localList: App_Levage_Updateable[],
    apiList: ApiReturn_Levage[],
  ): App_Levage_Updateable[] {
    const apiByFingerprint = new Map<string, ApiReturn_Levage>();
    for (const apiLevage of apiList) {
      apiByFingerprint.set(this.apiFingerprint(apiLevage), apiLevage);
    }

    let reconciled: App_Levage_Updateable[] = [];

    for (const local of localList) {
      if (!local.app_update.toUpdate) {
        reconciled.push(local);
        continue;
      }

      const fingerprint = this.pendingFingerprint(local);
      const apiMatch = apiByFingerprint.get(fingerprint);
      if (apiMatch) {
        reconciled.push(this.convertReturnedLevageIntoLevageUpdateable(apiMatch));
        continue;
      }

      reconciled.push(local);
    }

    reconciled = this.deduplicatePendingLevages(reconciled);
    return reconciled;
  }

  private deduplicatePendingLevages(levages: App_Levage_Updateable[]): App_Levage_Updateable[] {
    const keptFingerprints = new Set<string>();
    const result: App_Levage_Updateable[] = [];

    const sorted = [...levages].sort((a, b) => {
      const aPending = a.app_update.toUpdate ? 0 : 1;
      const bPending = b.app_update.toUpdate ? 0 : 1;
      if (aPending !== bPending) {
        return aPending - bPending;
      }
      const aNum = typeof a.data.id === 'number' ? 0 : 1;
      const bNum = typeof b.data.id === 'number' ? 0 : 1;
      return aNum - bNum;
    });

    for (const levage of sorted) {
      if (!levage.app_update.toUpdate) {
        result.push(levage);
        continue;
      }
      const fingerprint = this.pendingFingerprint(levage);
      if (keptFingerprints.has(fingerprint)) {
        continue;
      }
      keptFingerprints.add(fingerprint);
      result.push(levage);
    }

    return result;
  }

  private pendingFingerprint(levage: App_Levage_Updateable): string {
    const data = levage.app_update.data;
    const label = data?.levage_label ?? levage.data.levage_label ?? '';
    const formId = data?.form_id ?? levage.data.form_id;
    const etatId = data?.etat_id ?? levage.data.etat_id;
    const lastAction = data?.last_action_at ?? levage.data['last_action_at'] ?? '';
    return `${formId}|${label}|${etatId}|${lastAction}`;
  }

  private apiFingerprint(levage: ApiReturn_Levage): string {
    return `${levage.form_id}|${levage.levage_label ?? ''}|${levage.etat_id}|${levage['last_action_at'] ?? ''}`;
  }

  private isTemporaryLevageId(id: number | string | undefined): id is string {
    return typeof id === 'string' && id.startsWith('NEW_');
  }

  private buildLevageSendPayload(pending: App_Levage_Updateable): App_LevageSend {
    const data = { ...(pending.app_update.data as App_LevageSend) };
    const localId = pending.data.id;

    if (typeof localId === 'number') {
      data.levage_id = localId;
      delete data.client_temp_id;
    } else if (this.isTemporaryLevageId(localId)) {
      data.client_temp_id = localId;
      delete data.levage_id;
    } else if (typeof data.levage_id !== 'number' && typeof pending.data.id === 'number') {
      data.levage_id = pending.data.id;
    }

    return data;
  }

  private setLevagePendingState(id: number | string, toUpdate: boolean): Promise<void> {
    return firstValueFrom(this.getAllLevagesFromStorage()).then(levageListe => {
      const list = levageListe || [];
      const index = list.findIndex(levage => levage.data.id === id);
      if (index === -1) {
        return;
      }
      const updated: App_Levage_Updateable = {
        ...list[index],
        app_update: {
          ...list[index].app_update,
          toUpdate,
        },
      };
      const newList = [
        ...list.slice(0, index),
        updated,
        ...list.slice(index + 1),
      ];
      return this.dataService.levageListe.set(newList).then(() => undefined);
    });
  }

  private reassignFieldListenersAfterIdReplace(
    oldId: number | string,
    newId: number | string,
  ): Promise<void> {
    if (oldId === newId) {
      return Promise.resolve();
    }
    return firstValueFrom(this.dataService.fieldListenersListe.get()).then(list => {
      if (!list?.length) {
        return;
      }
      const updated = list.map(entry =>
        entry.id_levage === oldId ? { ...entry, id_levage: newId } : entry
      );
      return this.dataService.fieldListenersListe.set(updated).then(() => undefined);
    });
  }

  convertReturnedLevageIntoLevageUpdateable(returnedLevage: ApiReturn_Levage) {
    const levageUpdateable: App_Levage_Updateable = {
      app_update: {
        toUpdate: false
      },
      data: returnedLevage
    };
    return levageUpdateable;
  }

  checkAutorisationHabilitation(optionsListe: any[], validator: Type_App_AsyncValidator_CheckAutorisationsHabilitations): AsyncValidatorFn {
    const parameter = validator.parameter
    const error_message = typeof validator.error_message === 'string' ? validator.error_message : `Autorisations et habilitations non valides`; // TODO-pf configurer le message d'erreur en fonction de nouvelles données d'entrée
    return formControl => {
      if (formControl.value === null || typeof parameter !== 'string') {
        return of(null);
      }
      const found = optionsListe.find(option => option.value === formControl.value)
      if (found !== undefined) {
        const habilitations = found.habilitations;
        if (Array.isArray(habilitations)) {
          const foundHabilitation = habilitations.find(habilitation => habilitation.code === parameter)
          if (foundHabilitation) {
            const { date_delivrement, date_expiration } = foundHabilitation;
            const regexpFrenchDate = /^\d{2}\/\d{2}\/\d{4}/;
            if (typeof date_delivrement === 'string'
              && regexpFrenchDate.test(date_delivrement)
              && typeof date_expiration === 'string'
              && regexpFrenchDate.test(date_expiration)
            ) {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const dateDelivrement = this.generateDateFromDateFormat(date_delivrement);
              const dateExpiration = this.generateDateFromDateFormat(date_expiration);
              if (dateDelivrement !== undefined && dateExpiration !== undefined) {
                const todayTime = today.getTime()
                const dateDelivrementTime = dateDelivrement.getTime();
                const dateExpirationTime = dateExpiration.getTime();
                return todayTime >= dateDelivrementTime && todayTime <= dateExpirationTime
                ? of(null)
                : of({
                  err: {
                    icon: [`fas`, `circle-check`],
                    text: error_message,
                    type: `check-autorisations-habilitations`,
                  }
                })
              }
            }
          }
        }
      }
      return of({
        err: {
          icon: [`fas`, `circle-check`],
          text: error_message,
          type: `check-autorisations-habilitations`,
        }
      })
    }
  }

  checkValueIsAnArrayOrNull(): ValidatorFn {
    return formControl => {
      return formControl.value === null || Array.isArray(formControl.value)
      ? null
      : {
        err: {
          icon: [`fas`, `circle-check`],
          text: `Erreur donnée des marqueurs`,
          type: `value-bad-format-arr`,
        }
      };
    }
  }


  checkValueIsAnArrayRespectingMin(minMaxLength: number): ValidatorFn {
    return formControl => {
      const value = formControl.value;
      return Array.isArray(value) && value.length >= minMaxLength
      ? null
      : {
        err: {
          icon: [`fas`, `circle-check`],
          text: `Marqueurs manquants`,
          type: `value-bad-format-min`,
        }
      };
    }
  }

  checkValueIsAnArrayRespectingMax(minMaxLength: number): ValidatorFn {
    return formControl => {
      const value = formControl.value;
      return Array.isArray(value) && value.length <= minMaxLength
      ? null
      : {
        err: {
          icon: [`fas`, `circle-check`],
          text: `Nombre de marqueurs dépassant la limite`,
          type: `value-bad-format-max`,
        }
      };
    }
  }

  // 🔹 Helpers très simples
  normScalar(v: any): string | number {
    if (v == null) return '';
    if (typeof v === 'number') return v;
    if (v instanceof Date) return v.getTime();
    const s = String(v).trim();
    if (s === '') return '';
    // tente un nombre en tolérant la virgule
    const sNum = s.replace(',', '.');
    const n = Number(sNum);
    return !Number.isNaN(n) && /^-?\d+(\.\d+)?$/.test(sNum) ? n : s.toLowerCase();
  }
  parseNumLoose(v: any): number | null {
    if (v == null) return null;
    const sNum = String(v).trim().replace(',', '.');
    const n = Number(sNum);
    return Number.isNaN(n) ? null : n;
  }
  checkValueIsDifferentFromSources(validator: Type_App_Validator_ValueDifferentFrom, pageFormFunc: () => FormGroup | undefined): ValidatorFn {
    const sources = validator.sources;
    // Pas de fallback textuel hardcodé : si le JSON du formulaire ne configure pas `error_message`
    // pour ce validateur, on reste silencieux (texte vide filtré côté template par les composants de champ).
    const error_message = typeof validator.error_message === 'string' ? validator.error_message : '';
    console.log(`checkValueIsDifferentFromSources`, sources, error_message)
    return formControl => {
      const fieldsValues = sources.reduce<{ field: AbstractControl, operation: "=" | ">" | "<" | ">=" | "<=" }[]>((acc, val) => {
        const field = this.findFieldInFormEntity(pageFormFunc(), val.champ);
        if (field instanceof AbstractControl) {
          return [
            ...acc,
            {
              field,
              operation: val.operation
            }
          ];
        }
        return acc;
      }, [])

      if (formControl.value === null || formControl.value === undefined) {
        return null;
      }
      return fieldsValues.every(elem => {
        if (elem.operation === '=') {
          return formControl.value !== elem.field.value
        } else {
          const valAsNum = Number(formControl.value);
          const elemvalAsNum = Number(elem.field.value);
          //console.log(`checkValueIsDifferentFromSources`, { valAsNum, elemvalAsNum, operation: elem.operation })
          //console.log('operation',elemvalAsNum ,elem.operation, valAsNum)
          if (!Number.isNaN(valAsNum)) {
            if (!Number.isNaN(elemvalAsNum)) {
              if (elem.operation === '>') {
                //return valAsNum > elemvalAsNum;

                return elemvalAsNum  > valAsNum; // Inverted logic to match the original intent
              }
              if (elem.operation === '<') {
                //return valAsNum < elemvalAsNum;
                return elemvalAsNum < valAsNum; // Inverted logic to match the original intent
              }
              if (elem.operation === '>=') {
                //return valAsNum >= elemvalAsNum;
                return elemvalAsNum >= valAsNum; // Inverted logic to match the original intent
              }
              if (elem.operation === '<=') {
                //return valAsNum <= elemvalAsNum;
                return elemvalAsNum <= valAsNum; // Inverted logic to match the original intent
              }
              return true
            } else {
              return true;
            }
          } else {
            return true;
          }
        }
      })
      ? null
      : {
        err: {
          icon: [`fas`, `circle-check`],
          text: error_message,
          type: `value-different-from`,
        }
      };
    }
  }
//   checkValueIsDifferentFromSources(
//   validator: Type_App_Validator_ValueDifferentFrom,
//   pageFormFunc: () => FormGroup | undefined
// ): ValidatorFn {
//   const sources = validator.sources;
//   const error_message =
//     typeof validator.error_message === 'string'
//       ? validator.error_message
//       : `erreur`;

//   console.log('[VDF] init checkValueIsDifferentFromSources', { sources, error_message });

//   return formControl => {
//     const pageForm = pageFormFunc?.();
//     const fieldsValues = sources.reduce<
//       { field: AbstractControl; operation: '=' | '>' | '<' | '>=' | '<=' }[]
//     >((acc, val) => {
//       const field = this.findFieldInFormEntity(pageForm, val.champ);
//       console.log('[VDF] resolve source', val?.champ, '->', field);
//       if (field instanceof AbstractControl) {
//         acc.push({ field, operation: val.operation });
//       }
//       return acc;
//     }, []);

//     const rawTarget = formControl.value;
//     if (rawTarget === null || rawTarget === undefined) {
//       console.log('[VDF] target is null/undefined -> valid');
//       return null;
//     }

//     // ⚙️ Normalisation de la valeur cible
//     const target = this.normScalar(rawTarget);

//     // 🔎 Évalue chaque règle
//     const allOk = fieldsValues.every(elem => {
//       const rawSource = elem.field.value;
//       const op = elem.operation;

//       if (op === '=') {
//         const source = this.normScalar(rawSource);
//         const ok = target !== source;
//         console.log('[VDF] "=" compare', { target, source, ok });
//         return ok;
//       } else {
//         const a = this.parseNumLoose(rawTarget);
//         const b = this.parseNumLoose(rawSource);
//         // si l’un n’est pas numérique, on ne bloque pas la validation de ce critère
//         if (a === null || b === null) {
//           console.log('[VDF] numeric op with non-numeric, skip', { op, a, b });
//           return true;
//         }
//         let ok = true;
//         if (op === '>')  ok = a >  b;
//         if (op === '<')  ok = a <  b;
//         if (op === '>=') ok = a >= b;
//         if (op === '<=') ok = a <= b;
//         console.log('[VDF] numeric compare', { op, a, b, ok });
//         return ok;
//       }
//     });

//     if (allOk) {
//       console.log('[VDF] VALID');
//       return null;
//     }

//     console.warn('[VDF] INVALID ->', error_message, { target: rawTarget, fieldsValues });
//     return {
//       err: {
//         icon: ['fas', 'circle-check'],
//         text: error_message,
//         type: 'value-different-from',
//       },
//     };
//   };
// }

  checkValueIsNotEqualTo(validator: Type_App_Validator_ValueNotEqualTo): ValidatorFn {
    const values = validator.values;
    const error_message = typeof validator.error_message === 'string' ? validator.error_message : `Valeur non conforme`
    return formControl => {
      return values.every(value => formControl.value !== value)
      ? null
      : {
        err: {
          icon: [`fas`, `circle-check`],
          text: error_message,
          type: `value-not-equal-to`,
        }
      }
    }
  }

  requiredTrue(): ValidatorFn {
    return formControl => formControl.value === true
      ? null
      : {
        err: {
          text: `Le champ est invalide`,
          type: `requiredTrue`,
        }
      }
  }

  generateDateFromDateFormat(dateStr: string): Date | undefined {
    const regexpFrenchDate = /^\d{2}\/\d{2}\/\d{4}/;
    if (regexpFrenchDate.test(dateStr)) {
      const year = dateStr.slice(6, 10)
      const month = dateStr.slice(3, 5)
      const day = dateStr.slice(0, 2)
      const date = new Date(`${year}-${month}-${day}`);
      if (isValidDate(date)) {
        return date;
      }
    }
    return undefined;
  }

  generateSketchCustomGroup(field: App_FormSketchField, dataLevage: App_Levage_Updateable | undefined, pageFormFunc: () => FormGroup | undefined, pageObserveSourcesArr: { formControl: FormControl, validator: Type_App_Validator; }[], context: 'preparation' | 'edition') {
    const initialValueFromEvaluate = evaluateInitialvalue(field, dataLevage);
    const initialValue = Array.isArray(initialValueFromEvaluate) ? initialValueFromEvaluate : [];
    const image = field.fields.image;
    const drawing = field.fields.drawing;
    const markers = field.fields.markers;
    const prototypeOnAddMarker = field.fields.prototypeOnAddMarker;

    const formGroup = this.formBuilder.group({});
    if (image && typeof image !== 'string') {
      const imageInitialValue = Array.isArray(initialValue)
      ? initialValue.find(val => val?.name === image.name)?.value || null
      : null;
      const formControl = this.generateForm(image, undefined, pageFormFunc, pageObserveSourcesArr, context);
      const defaultImage = typeof field.fields.image_default === 'string' && field.fields.image_default.length > 0
        ? field.fields.image_default
        : null;
      formControl.setValue(imageInitialValue || defaultImage);
      formGroup.addControl(image.name, formControl);
    }
    if (drawing) {
      const drawingInitialValue = Array.isArray(initialValue)
      ? initialValue.find(val => val?.name === drawing.name)?.value || null
      : null;
      const formControl = this.generateForm(drawing, undefined, pageFormFunc, pageObserveSourcesArr, context);
      formControl.setValue(drawingInitialValue);
      formGroup.addControl(drawing.name, formControl);
    }
    if (markers) {
      const markersInitialValue = Array.isArray(initialValue)
      ? initialValue.find(val => val?.name === markers.name)?.value || null
      : null;
      const formControl = this.generateForm(markers, undefined, pageFormFunc, pageObserveSourcesArr, context);
      formControl.setValue(markersInitialValue);
      formGroup.addControl(markers.name, formControl);
    }
    if (prototypeOnAddMarker) {
      const prototypeOnAddMarkerInitialValue = Array.isArray(initialValue)
      ? initialValue.find(val => val?.name === prototypeOnAddMarker.name)?.value || null
      : null;
      const formArray = this.generateSketchFormDynamiqueArray(prototypeOnAddMarker, prototypeOnAddMarkerInitialValue, pageFormFunc, pageObserveSourcesArr, context);

      formGroup.addControl(prototypeOnAddMarker.name, formArray)
    }
    // TODO-pf? autres champs s'ils se présentent
    return formGroup;
  }

  generateSketchFormDynamiqueArray(formSketchFormDynamique: App_FormSketchFormDynamique, prototypeOnAddMarkerInitialValue: any, pageFormFunc: () => FormGroup | undefined, pageObserveSourcesArr: { formControl: FormControl, validator: Type_App_Validator; }[], context: 'preparation' | 'edition'): FormArray {
    const formArray = this.formBuilder.array<FormGroup>([]);
    const initialValue = evaluateInitialValueDynamicForm(formSketchFormDynamique, prototypeOnAddMarkerInitialValue);
    if (Array.isArray(initialValue)) {
      for (const element of initialValue) {
        const formGroup = this.generateFormDynamiqueSubGroupe(formSketchFormDynamique, element, pageFormFunc, pageObserveSourcesArr, context);
        formArray.push(formGroup);
      }
    }
    return formArray;
  }

  generateFormDynamiqueSubGroupe(formSketchFormDynamique: App_FormSketchFormDynamique, formGroupInitialValue: any, pageFormFunc: () => FormGroup | undefined, pageObserveSourcesArr: { formControl: FormControl, validator: Type_App_Validator; }[], context: 'preparation' | 'edition'): FormGroup {
    const formGroup = this.formBuilder.group({});
    formSketchFormDynamique.champs.forEach(field => {
      const isTrueField = instanceOfTrueField(field)
      if (isTrueField) {
        const formControl = this.generateForm(field, undefined, pageFormFunc, pageObserveSourcesArr, context);
        if (typeof field?.name === 'string' && formGroupInitialValue && formGroupInitialValue[field.name]) {
          formControl.setValue(formGroupInitialValue[field.name]);
        }
        formGroup.addControl(field.name, formControl);
      }
    });
    return formGroup;
  }

}
